/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useCallback, useMemo } from 'react';
import { ModalAwareScrollView } from '../../shared/modals/ModalAwareScrollView';
import { Modal } from '../../shared/modals/Modal';
import { Pressable, StyleSheet, useWindowDimensions, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { ProminentSearchWithScanner } from '../../shared/ui/ProminentSearchWithScanner';
import { MIN_PRODUCT_SEARCH_QUERY_CHARS, effectiveProductSearchQuery } from '../../shared/product-search/productSearchUx';
import {
  ProductSearchSuggestionsPanel,
  type ProductSearchSuggestionItem,
} from '../../shared/product-search/components/ProductSearchSuggestionsPanel';
import { spacing } from '../../shared/theme/spacing';
import { ticketColors } from '../../shared/theme/ticketColors';
import { posSharedUi as posUi } from '../../shared/ui/posSharedUi';
import { modalShellShared } from '../../shared/modals/modalShellShared.styles';
import { useModalBackdropPadding } from '../../shared/ui/hooks-ui/useModalBackdropPadding';
import type { StockRow } from '../../inventory/stock/public-api';
import { TicketModalHeader } from '../../shared/ticket/TicketModalHeader'
import { ModalStatePanel } from '../../shared/modals/ModalStatePanel'
import { modalExperience } from '../../shared/modals/modalExperience.styles';
import { formatStockQuantity } from '../application/inventoryUtils';

type Props = {
  visible: boolean;
  searchQuery: string;
  searchQueryDebounced: string;
  onSearchChange: (q: string) => void;
  stocks: StockRow[];
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  onSelect: (row: StockRow) => void;
  onClose: () => void;
  onOpenScanner?: () => void;
  showBarcodeCameraButton?: boolean;
  onSearchSubmitEditing?: () => void;
  autoFocusSearchOnWeb?: boolean;
};

export const TransferProductPickerModal = ({
  visible,
  searchQuery,
  searchQueryDebounced,
  onSearchChange,
  stocks,
  loading,
  loadingMore,
  hasMore,
  onLoadMore,
  onSelect,
  onClose,
  onOpenScanner,
  showBarcodeCameraButton = true,
  onSearchSubmitEditing,
  autoFocusSearchOnWeb = false,
}: Props) => {
  const modalBackdropPadding = useModalBackdropPadding();
  const { width } = useWindowDimensions();
  const isMobile = width < 768;
  const insets = useSafeAreaInsets();

  const suggestionsOpen = effectiveProductSearchQuery(searchQueryDebounced).length > 0;

  const suggestionItems = useMemo<ProductSearchSuggestionItem[]>(
    () =>
      stocks.map((row) => ({
        id: row.id,
        title: row.productName ?? row.productId,
        subtitle: `${row.productSku ?? '—'} · ${formatStockQuantity(row.quantity, row.unitOfMeasure)}`,
        imageUrl: row.imageUrl ?? null,
      })),
    [stocks]
  );

  const handlePickSuggestion = useCallback(
    (item: ProductSearchSuggestionItem) => {
      const row = stocks.find((s) => s.id === item.id);
      if (row) onSelect(row);
    },
    [onSelect, stocks]
  );

  const suggestionFooter = (
    <ProductSearchSuggestionsPanel
      open={suggestionsOpen}
      loading={loading && stocks.length === 0}
      items={suggestionItems}
      emptyMessage="Ningún producto con stock coincide. Probá otro término o mirá el catálogo."
      onPick={handlePickSuggestion}
      loadMoreLabel={hasMore ? 'Cargar más resultados' : undefined}
      onLoadMore={hasMore ? onLoadMore : undefined}
      loadingMore={loadingMore && stocks.length > 0}
    />
  );

  const showPreSearchHint = searchQuery.trim().length === 0;

  return (
    <Modal visible={visible} transparent animationType={isMobile ? 'slide' : 'fade'} onRequestClose={onClose}>
        <View style={[posUi.modalBackdrop, isMobile ? { justifyContent: 'flex-end' } : modalExperience.modalRoot, isMobile ? null : modalBackdropPadding]}>
          {isMobile ? <Pressable style={StyleSheet.absoluteFill} onPress={onClose} /> : null}
          <View
            style={[isMobile ? [styles.sheetCard, { paddingBottom: Math.max(insets.bottom, spacing.sm) }] : [posUi.modalCard, modalShellShared.modalCardShell, modalExperience.modalWidthStandard]]}
          >
          {isMobile ? <View style={styles.grabber} /> : null}
          <ModalAwareScrollView
            style={isMobile ? styles.sheetScroll : undefined}
            contentContainerStyle={isMobile ? styles.sheetScrollContent : undefined}
            keyboardShouldPersistTaps="handled"
            showsVerticalScrollIndicator={false}
          >
          <TicketModalHeader
            kicker="Stock · Transferencia"
            title="Elegí producto"
            subtitle="Después confirmás cantidad y sucursal destino."
            onClose={onClose}
            compact={isMobile}
          />
          <ProminentSearchWithScanner
            sectionLabel={null}
            unifiedField
            scanTone="neutral"
            value={searchQuery}
            onChangeText={onSearchChange}
            onPressScan={onOpenScanner}
            showBarcodeCameraButton={showBarcodeCameraButton && Boolean(onOpenScanner)}
            placeholder={`Nombre, SKU o código (min. ${MIN_PRODUCT_SEARCH_QUERY_CHARS} caracteres)`}
            placeholderCompact={`Producto (${MIN_PRODUCT_SEARCH_QUERY_CHARS}+)`}
            accessibilityLabel="Buscar producto a transferir"
            onSubmitEditing={onSearchSubmitEditing}
            autoFocusOnWeb={false}
            footer={suggestionFooter}
          />
          {showPreSearchHint ? (
            <ModalStatePanel
              empty
              emptyTitle="Buscar producto"
              emptyMessage="Escribí nombre, SKU o escaneá el código. Solo mostramos productos con stock acá."
            />
          ) : null}
          </ModalAwareScrollView>
          </View>
        </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  sheetCard: {
    flex: 1,
    width: '100%',
    maxHeight: '92%',
    backgroundColor: ticketColors.paper2,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    borderTopWidth: 1,
    borderColor: ticketColors.ruleSoft,
    paddingTop: 8,
  },
  sheetScroll: {
    flex: 1,
  },
  sheetScrollContent: {
    paddingHorizontal: spacing.md,
    paddingBottom: spacing.xl,
  },
  grabber: {
    width: 36,
    height: 4,
    borderRadius: 2,
    backgroundColor: ticketColors.rule,
    alignSelf: 'center',
    marginBottom: spacing.xs,
  },
});
